import React, { Fragment, useState, useEffect, useRef, useCallback, useContext } from 'react'
import { gql, useQuery, useLazyQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { Row } from 'react-bootstrap'
import { AuthContext } from '../context/auth-context'
import { GuideContext } from '../context/guide-context'
import Card from '../components/common/Card'
import AudienceCounter from '../components/dashboard/AudienceCounter'
import AudienceChart from '../components/dashboard/AudienceChart'
import AudiencePieChart from '../components/dashboard/AudiencePieChart'
import PlatformAudienceChart from '../components/dashboard/PlatformAudienceChart'
import AudienceBySegmentChart from '../components/dashboard/AudienceBySegmentChart'
import { CubeLoader } from '@/components/ui/cube-loader'
import _ from 'lodash'
import 'flatpickr/dist/flatpickr.css'
import { DashbordIcon } from '@/components/icons/new'

const GET_USER = gql`
    query Users($where: UserWhere) {
        users(where: $where) {
            id
            paymentMethodId
            address
            phone
        }
    }
`

const GET_PLATFORMS = gql`
    query Platforms($options: PlatformOptions, $where: PlatformWhere) {
        platforms(options: $options, where: $where) {
            id
            name
        }
    }
`

const GET_CAMPAIGN = gql`
    query Campaigns($where: CampaignWhere) {
        campaigns(where: $where) {
            keywords
            audience
            audienceByKeywords
        }
    }
`

const GET_INSIGHTS = gql`
    query Publishers(
        $publishersWhere: PublisherWhere
        $adCampaignsWhere: AdCampaignWhere
        $options: AdCampaignOptions
    ) {
        publishers(where: $publishersWhere) {
            name
            adCampaigns(where: $adCampaignsWhere, options: $options) {
                conversion
                costPerConversion
                impressions
                uniqueCtr
                spend
                addToCart
                addToCartValue
                purchase
                purchaseValue
                createdAt
                landingPagesConnection {
                    edges {
                        sales
                        conversionRate
                        node {
                            id
                        }
                    }
                }
            }
        }
    }
`

const GET_CUSTOM_INSIGHTS = gql`
    query PublisherCustomInsights($from: DateTime!, $to: DateTime!) {
        publisherCustomInsights(from: $from, to: $to) {
            id
            name
            addToCart
            addToCartValue
            conversion
            costPerConversion
            uniqueCtr
            purchase
            purchaseValue
            spend
        }
    }
`

const Dashboard = () => {
    const { id } = useParams()
    const { user } = useContext(AuthContext)
    const { guide } = useContext(GuideContext)
    const navigate = useNavigate()
    const fp = useRef(null)
    const [totalAudience, setTotalAudience] = useState({})
    const [audience, setAudience] = useState({})
    const [keywords, setKeywords] = useState([])
    const [data, setData] = useState({
        spend: 0,
        landingPagesConnection: {
            edges: [],
        },
    })
    const [period, setPeriod] = useState(null)

    const userQuery = useQuery(GET_USER, {
        variables: { where: { id: user.id } },
        onCompleted: (data) => {
            // If this is a fresh user, redirect to profile.
            if (data?.users[0] && guide === 'on') {
                if (!data.users[0].paymentMethodId || !data.users[0].address || !data.users[0].phone) {
                    navigate('/profile')
                }
            }
        },
    })

    const [getCustomInsights, customInsights] = useLazyQuery(GET_CUSTOM_INSIGHTS, {
        fetchPolicy: 'network-only',
    })
    const campaign = useQuery(GET_CAMPAIGN, {
        variables: id ? { where: { id } } : {},
    })

    const insights = useQuery(GET_INSIGHTS, {
        variables: {
            adCampaignsWhere: {},
            options: { sort: [{ createdAt: 'ASC' }] },
        },
    })

    const platforms = useQuery(GET_PLATFORMS, {
        variables: { options: { sort: [{ order: 'ASC' }] }, where: { status: 'ACTIVE' } },
    })

    const combineData = useCallback((data) => {
        const combinedData = {}

        // Filter out Ad Campaigns without CTR data.
        const ctrData = data.filter((item) => item.uniqueCtr)

        // Calculate CTR as a weighted average based on impressions.
        combinedData.uniqueCtr =
            ctrData.reduce((sum, item) => sum + item.uniqueCtr * item.impressions, 0) /
            ctrData.reduce((sum, item) => sum + item.impressions, 0)

        combinedData.createdAt = data.reduce(
            (acc, obj) => (acc < new Date(obj.createdAt) ? acc : new Date(obj.createdAt)),
            new Date(data[0].createdAt),
        )
        combinedData.addToCart = data.reduce((acc, obj) => acc + obj.addToCart, 0)
        combinedData.addToCartValue = data.reduce((acc, obj) => acc + obj.addToCartValue, 0)
        combinedData.conversion = data.reduce((acc, obj) => acc + obj.conversion, 0)
        combinedData.purchase = data.reduce((acc, obj) => acc + obj.purchase, 0)
        combinedData.purchaseValue = data.reduce((acc, obj) => acc + obj.purchaseValue, 0)
        combinedData.spend = data.reduce((acc, obj) => acc + obj.spend, 0)

        combinedData.costPerConversion = combinedData.conversion > 0 ? combinedData.spend / combinedData.conversion : 0

        setData(combinedData)
    }, [])

    const defaultPeriod = useCallback((startDate) => {
        const start = startDate ? new Date(startDate) : new Date()
        const end = new Date()
        return (
            start.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            }) +
            ' - ' +
            end.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            })
        )
    }, [])

    const handleDateChange = async (selectedDates) => {
        // Check if both start and end dates are selected
        if (selectedDates.length === 2) {
            setPeriod(
                selectedDates[0].toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                }) +
                    ' - ' +
                    selectedDates[1].toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    }),
            )

            await getCustomInsights({
                variables: {
                    from: selectedDates[0].toISOString().split('T')[0],
                    to: selectedDates[1].toISOString().split('T')[0],
                },
                onCompleted: (response) => {
                    const data = response.publisherCustomInsights.find((publisher) => publisher.name === 'Meta')
                    setData((prevData) => ({
                        ...prevData,
                        ...data,
                    }))
                },
            })
        }
    }

    const openFlatpickr = () => {
        if (fp.current?.flatpickr) {
            setTimeout(() => fp.current.flatpickr.open(), 100)
        }
    }

    useEffect(() => {
        if (insights.data?.publishers?.length > 0) {
            const data = insights.data.publishers.find((publisher) => publisher.name === 'Meta').adCampaigns
            let startDate = null
            if (data.length > 0) {
                if (data.length > 1) {
                    combineData(data)
                } else {
                    setData(data[0])
                }
                startDate = data[0].createdAt
            }
            setPeriod(defaultPeriod(startDate))
        }
    }, [insights.data, combineData, setPeriod, defaultPeriod])

    useEffect(() => {
        if (!campaign.loading && campaign?.data?.campaigns && !platforms.loading && platforms?.data?.platforms) {
            let data

            if (campaign.data.campaigns.length > 0 && campaign.audience !== null) {
                data = campaign.data.campaigns.reduce((acc, campaign) => {
                    if (campaign.audience !== null) {
                        const campaignData = JSON.parse(campaign.audience)
                        Object.keys(campaignData).forEach((platformName) => {
                            if (!acc[platformName]) {
                                acc[platformName] = {
                                    audience: 0,
                                    audienceThisWeek: Array(7).fill(0),
                                    audienceThisMonth: Array(30).fill(0),
                                    audienceThisYear: Array(12).fill(0),
                                }
                            }
                            acc[platformName].audience += campaignData[platformName].audience
                            acc[platformName].audienceThisWeek = acc[platformName].audienceThisWeek.map(
                                (val, i) => val + campaignData[platformName].audienceThisWeek[i],
                            )
                            acc[platformName].audienceThisMonth = acc[platformName].audienceThisMonth.map(
                                (val, i) => val + campaignData[platformName].audienceThisMonth[i],
                            )
                            acc[platformName].audienceThisYear = acc[platformName].audienceThisYear.map(
                                (val, i) => val + campaignData[platformName].audienceThisYear[i],
                            )
                        })
                    }
                    return acc
                }, {})
                if (Object.keys(data).length === 0) {
                    data = Object.values(platforms.data.platforms).reduce((acc, platform) => {
                        acc[platform.name] = {
                            audience: 0,
                            audienceThisWeek: Array(7).fill(0),
                            audienceThisMonth: Array(30).fill(0),
                            audienceThisYear: Array(12).fill(0),
                        }
                        return acc
                    }, {})
                }
            } else {
                data = Object.values(platforms.data.platforms).reduce((acc, platform) => {
                    acc[platform.name] = {
                        audience: 0,
                        audienceThisWeek: Array(7).fill(0),
                        audienceThisMonth: Array(30).fill(0),
                        audienceThisYear: Array(12).fill(0),
                    }
                    return acc
                }, {})
            }

            setAudience(data)

            const year = []
            const month = []
            const week = []

            Object.values(data).forEach(({ audienceThisYear }) => {
                audienceThisYear.forEach((value, index) => {
                    if (year[index] === undefined) {
                        year[index] = value
                    } else {
                        year[index] += value
                    }
                })
            })

            Object.values(data).forEach(({ audienceThisMonth }) => {
                audienceThisMonth.forEach((value, index) => {
                    if (month[index] === undefined) {
                        month[index] = value
                    } else {
                        month[index] += value
                    }
                })
            })

            Object.values(data).forEach(({ audienceThisWeek }) => {
                audienceThisWeek.forEach((value, index) => {
                    if (week[index] === undefined) {
                        week[index] = value
                    } else {
                        week[index] += value
                    }
                })
            })

            setTotalAudience({
                audienceThisWeek: week,
                audienceThisMonth: month,
                audienceThisYear: year,
            })

            // Process keywords data for segment chart
            if (campaign.data.campaigns.length > 0) {
                // Aggregate audience by keywords across all campaigns
                const keywordsMap = new Map()
                campaign.data.campaigns.forEach((campaignItem) => {
                    if (campaignItem.audienceByKeywords !== null) {
                        const audienceByKeywords = JSON.parse(campaignItem.audienceByKeywords)
                        audienceByKeywords.forEach((keyword) => {
                            if (keywordsMap.has(keyword.value)) {
                                const existing = keywordsMap.get(keyword.value)
                                existing.audience += keyword.audience
                            } else {
                                keywordsMap.set(keyword.value, { ...keyword })
                            }
                        })
                    }
                })
                setKeywords(Array.from(keywordsMap.values()))
            }
        }
    }, [campaign, platforms])

    if (platforms.loading || campaign.loading || !audience || Object.keys(audience).length === 0)
        return <CubeLoader size="default" fullScreen={true} />

    return (
        <Fragment>
            <Card className="page-card">
                <Card.Header>
                    <div className="header-title d-flex align-items-center justify-content-center">
                        <i className="icon text-gray-200 me-1">
                            <DashbordIcon size={24} color="currentColor" />
                        </i>
                        <h5 className="title mb-0 text-white">Dashboard</h5>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <AudienceCounter platforms={platforms.data.platforms} data={audience} />
                        {!_.isEmpty(totalAudience) && (
                            <AudienceChart name={'Audience Built across All Platforms'} data={totalAudience} />
                        )}
                        <AudiencePieChart
                            name={'Audience Structure'}
                            data={platforms.data.platforms}
                            audience={audience}
                        />
                        <PlatformAudienceChart
                            name={'Audience Built per Platform'}
                            data={platforms.data.platforms}
                            audience={audience}
                        />
                        <AudienceBySegmentChart
                            name={'Audience by Segment'}
                            data={keywords}
                            keywords={campaign.data.campaigns.reduce((acc, campaignItem) => {
                                if (campaignItem.keywords) {
                                    const campaignKeywords = JSON.parse(campaignItem.keywords)
                                    return [...acc, ...campaignKeywords]
                                }
                                return acc
                            }, [])}
                        />
                    </Row>
                </Card.Body>
            </Card>
        </Fragment>
    )
}

export default Dashboard
